import { DEMO_STORE_KEY, SEED_VERSION } from './constants';
import type { DemoRow, DemoStore } from './types';

/**
 * The demo's database: tables in memory, mirrored to sessionStorage so a
 * reload does not discard the ride the visitor just booked.
 *
 * sessionStorage, not localStorage: the demo belongs to its tab.
 */

let store: DemoStore | null = null;

interface Persisted {
  version: number;
  tables: Record<string, DemoRow[]>;
}

function load(): DemoStore | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(DEMO_STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Persisted;
    // A store written by an older generator has the wrong shape for today's
    // screens; discard rather than half-render it.
    if (parsed.version !== SEED_VERSION) return null;
    return new Map(Object.entries(parsed.tables));
  } catch {
    return null;
  }
}

function persist(next: DemoStore): void {
  try {
    const tables = Object.fromEntries(next.entries());
    globalThis.sessionStorage?.setItem(
      DEMO_STORE_KEY,
      JSON.stringify({ version: SEED_VERSION, tables } satisfies Persisted),
    );
  } catch {
    /* Quota or private mode: the in-memory store still works for this page. */
  }
}

export function getStore(build: () => DemoStore): DemoStore {
  if (store) return store;
  store = load() ?? build();
  persist(store);
  return store;
}

/** Call after any mutation. Stage 3 hangs the realtime emitter off this. */
export function commit(): void {
  if (store) persist(store);
}

export function resetStore(build: () => DemoStore): DemoStore {
  store = build();
  persist(store);
  return store;
}

export function forgetStore(): void {
  store = null;
  try {
    globalThis.sessionStorage?.removeItem(DEMO_STORE_KEY);
  } catch {
    /* nothing to do */
  }
}
